import React, { useEffect, useState } from 'react';
import { auditFetch } from '../utils/auditFetch';
import '../styles/Dashboard.css';

const createProvider = (index = 1) => ({
  id: `provider-${Date.now()}-${index}`,
  label: `Proveedor ${index}`,
  kind: 'livekit',
  enabled: true,
  settings: {
    url: '',
    apiKey: '',
    apiSecret: '',
    recordingDir: 'recordings',
    notes: '',
  },
});

const VideoProviderSettingsPanel = ({ user }) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const [settings, setSettings] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    auditFetch(`${backendUrl}/api/admin/platform-settings`, {}, user)
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('No se pudo cargar la configuracion de proveedores'))))
      .then((data) => {
        if (!cancelled) setSettings(data.settings || null);
      })
      .catch((error) => {
        console.error('Error loading platform settings:', error);
        if (!cancelled) setStatusMessage('No se pudo cargar la configuracion operativa.');
      });

    return () => {
      cancelled = true;
    };
  }, [backendUrl, user]);

  const providers = settings?.video?.providers || [];
  const activeProvider = providers.find((provider) => provider.id === settings?.video?.activeProviderId) || null;
  const recording = settings?.recording || {};
  const recordingConfigured = recording.storageMode === 'local'
    ? Boolean(recording.localDir || 'recordings')
    : Boolean(recording.s3?.bucket && recording.s3?.accessKey && recording.s3?.secretKey);

  const updateSettings = (updater) => {
    setSettings((current) => {
      if (!current) return current;
      return typeof updater === 'function' ? updater(current) : updater;
    });
  };

  const updateProvider = (providerId, field, value) => {
    updateSettings((current) => ({
      ...current,
      video: {
        ...current.video,
        providers: current.video.providers.map((provider) => (
          provider.id === providerId ? { ...provider, [field]: value } : provider
        )),
      },
    }));
  };

  const updateProviderSetting = (providerId, field, value) => {
    updateSettings((current) => ({
      ...current,
      video: {
        ...current.video,
        providers: current.video.providers.map((provider) => (
          provider.id === providerId
            ? { ...provider, settings: { ...provider.settings, [field]: value } }
            : provider
        )),
      },
    }));
  };

  const updateRecording = (field, value) => {
    updateSettings((current) => ({
      ...current,
      recording: {
        ...(current.recording || {}),
        [field]: value,
      },
    }));
  };

  const updateRecordingS3 = (field, value) => {
    updateSettings((current) => ({
      ...current,
      recording: {
        ...(current.recording || {}),
        s3: {
          ...(current.recording?.s3 || {}),
          [field]: value,
        },
      },
    }));
  };

  const addProvider = () => {
    updateSettings((current) => ({
      ...current,
      video: {
        ...current.video,
        providers: [...current.video.providers, createProvider(current.video.providers.length + 1)],
      },
    }));
  };

  const removeProvider = (providerId) => {
    updateSettings((current) => {
      const nextProviders = current.video.providers.filter((provider) => provider.id !== providerId);
      const nextActiveProviderId = current.video.activeProviderId === providerId
        ? (nextProviders[0]?.id || '')
        : current.video.activeProviderId;
      return {
        ...current,
        video: {
          ...current.video,
          activeProviderId: nextActiveProviderId,
          providers: nextProviders,
        },
      };
    });
  };

  const saveSettings = async () => {
    if (!settings) return;
    setIsSaving(true);
    setStatusMessage('');
    try {
      const response = await auditFetch(`${backendUrl}/api/admin/platform-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      }, user);
      if (!response.ok) throw new Error('No se pudo guardar la configuracion');
      const data = await response.json();
      setSettings(data.settings || settings);
      setStatusMessage('Configuracion operativa guardada. Las nuevas consultas usaran este proveedor activo.');
    } catch (error) {
      console.error('Error saving platform settings:', error);
      setStatusMessage('No se pudo guardar la configuracion operativa.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!settings) {
    return (
      <section className="institutional-settings-panel">
        <div className="section-header">
          <div>
            <h2>Proveedores de video</h2>
            <p>Cargando configuracion operativa...</p>
          </div>
        </div>
        {statusMessage && <p className="clinical-files-message">{statusMessage}</p>}
      </section>
    );
  }

  return (
    <section className="institutional-settings-panel">
      <div className="section-header">
        <div>
          <h2>Proveedores de video</h2>
          <p>Configura videollamada y grabacion desde la aplicacion. Para VPS, usa LiveKit y storage local para empezar.</p>
        </div>
        <div className="admin-dashboard-actions">
          <button className="btn btn-secondary" type="button" onClick={addProvider}>
            Agregar proveedor
          </button>
          <button className="btn btn-primary" type="button" onClick={saveSettings} disabled={isSaving}>
            {isSaving ? 'Guardando...' : 'Guardar configuracion'}
          </button>
        </div>
      </div>

      {statusMessage && <p className="clinical-files-message">{statusMessage}</p>}

      <div className="livekit-config-guide">
        <div>
          <strong>Que debes poner en LiveKit</strong>
          <p><span>URL:</span> direccion de tu servidor o LiveKit Cloud, por ejemplo https://mi-livekit.com o https://xxx.livekit.cloud.</p>
          <p><span>API key:</span> clave publica del proyecto LiveKit. No es la URL.</p>
          <p><span>API secret:</span> secreto privado de LiveKit. Debe guardarse solo en backend.</p>
        </div>
        <div>
          <strong>Como funciona la grabacion</strong>
          <p>La app genera token, conecta paciente/medico a la sala consultation-ID, y si hay consentimiento aceptado puede iniciar Egress automaticamente.</p>
          <p><span>Local VPS:</span> guarda MP4 en la carpeta configurada y permite ver/descargar desde auditoria.</p>
          <p><span>S3/MinIO:</span> LiveKit envia el MP4 al bucket. La lectura protegida requiere URL firmada/proxy como siguiente paso.</p>
        </div>
      </div>

      <div className="institutional-settings-grid">
        <div className="institutional-settings-form">
          <label className="institutional-field-wide">
            Proveedor activo
            <select
              value={settings.video.activeProviderId || ''}
              onChange={(event) => updateSettings((current) => ({
                ...current,
                video: { ...current.video, activeProviderId: event.target.value },
              }))}
            >
              {providers.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.label} · {provider.kind}{provider.enabled ? '' : ' · deshabilitado'}
                </option>
              ))}
            </select>
          </label>
          <p className="admin-range-label">
            Activo ahora: {activeProvider ? `${activeProvider.label} (${activeProvider.kind})` : 'Sin proveedor seleccionado'}
          </p>
          <p className="admin-range-label">
            Usa `p2p` solo para llamadas sin servidor de video. Usa `livekit` para grabar, auditar eventos y mejorar estabilidad.
          </p>
        </div>

        <div className="institutional-settings-form">
          <div className="section-header">
            <div>
              <h3>Grabacion de teleconsultas</h3>
              <p>Controla grabacion automatica con consentimiento y destino de almacenamiento.</p>
            </div>
            <span className={`recording-config-status ${recordingConfigured ? 'ready' : 'pending'}`}>
              {recordingConfigured ? 'Configurado' : 'Incompleto'}
            </span>
          </div>
          <label className="platform-provider-toggle">
            <input
              type="checkbox"
              checked={Boolean(recording.enabled)}
              onChange={(event) => updateRecording('enabled', event.target.checked)}
            />
            <small>Habilitar grabacion</small>
          </label>
          <p className="config-help-text">Activa esta opcion solo cuando LiveKit ya este configurado y el consentimiento digital se use antes de entrar a consulta.</p>
          <label className="platform-provider-toggle">
            <input
              type="checkbox"
              checked={Boolean(recording.autoStartWithConsent)}
              onChange={(event) => updateRecording('autoStartWithConsent', event.target.checked)}
            />
            <small>Iniciar automaticamente con consentimiento aceptado</small>
          </label>
          <p className="config-help-text">Si esta activo, el medico no debe presionar grabar. La app inicia la grabacion cuando la consulta LiveKit esta conectada y existe consentimiento aceptado.</p>
          <label>
            Storage
            <select value={recording.storageMode || 'local'} onChange={(event) => updateRecording('storageMode', event.target.value)}>
              <option value="local">Local VPS</option>
              <option value="s3">S3</option>
              <option value="minio">MinIO</option>
            </select>
          </label>
          <p className="config-help-text">Para VPS inicial selecciona Local VPS. S3/MinIO es para almacenamiento externo compatible con S3.</p>
          <label>
            Directorio local
            <input
              value={recording.localDir || 'recordings'}
              onChange={(event) => updateRecording('localDir', event.target.value)}
              placeholder="recordings"
            />
          </label>
          <p className="config-help-text">Ruta relativa o absoluta donde LiveKit Egress escribira el MP4. Ejemplo simple: recordings.</p>
          <label>
            Webhook secret LiveKit
            <input
              value={recording.webhookSecret || ''}
              onChange={(event) => updateRecording('webhookSecret', event.target.value)}
              placeholder="Opcional si se usa API secret del proveedor"
            />
          </label>
          <p className="config-help-text">Normalmente LiveKit firma webhooks con API key/secret. Este campo queda como referencia operativa si usas un secreto separado.</p>
          <div className="recording-storage-grid">
            <input value={recording.s3?.endpoint || ''} onChange={(event) => updateRecordingS3('endpoint', event.target.value)} placeholder="S3/MinIO endpoint" />
            <input value={recording.s3?.bucket || ''} onChange={(event) => updateRecordingS3('bucket', event.target.value)} placeholder="Bucket" />
            <input value={recording.s3?.region || 'us-east-1'} onChange={(event) => updateRecordingS3('region', event.target.value)} placeholder="Region" />
            <input value={recording.s3?.accessKey || ''} onChange={(event) => updateRecordingS3('accessKey', event.target.value)} placeholder="Access key" />
            <input value={recording.s3?.secretKey || ''} onChange={(event) => updateRecordingS3('secretKey', event.target.value)} placeholder="Secret key" />
            <label className="platform-provider-toggle">
              <input
                type="checkbox"
                checked={Boolean(recording.s3?.forcePathStyle)}
                onChange={(event) => updateRecordingS3('forcePathStyle', event.target.checked)}
              />
              <small>Force path style</small>
            </label>
          </div>
        </div>

        <div className="platform-provider-grid">
          {providers.map((provider) => (
            <div key={provider.id} className="platform-provider-card">
              <strong>{provider.label}</strong>
              <p className="config-help-text">Proveedor usado para nuevas consultas cuando esta habilitado y seleccionado como activo.</p>
              <span>
                <input
                  value={provider.label}
                  onChange={(event) => updateProvider(provider.id, 'label', event.target.value)}
                  placeholder="Nombre visible"
                />
              </span>
              <span>
                <select value={provider.kind} onChange={(event) => updateProvider(provider.id, 'kind', event.target.value)}>
                  <option value="p2p">p2p</option>
                  <option value="livekit">livekit</option>
                  <option value="custom">custom</option>
                </select>
              </span>
              <span>
                <input
                  value={provider.id}
                  onChange={(event) => updateProvider(provider.id, 'id', event.target.value)}
                  placeholder="Identificador"
                />
              </span>
              <span>
                <input
                  value={provider.settings?.url || ''}
                  onChange={(event) => updateProviderSetting(provider.id, 'url', event.target.value)}
                  placeholder="URL / WSS / endpoint"
                />
                <small>Ejemplo: https://xxx.livekit.cloud o https://livekit.tudominio.com</small>
              </span>
              <span>
                <input
                  value={provider.settings?.apiKey || ''}
                  onChange={(event) => updateProviderSetting(provider.id, 'apiKey', event.target.value)}
                  placeholder="API key"
                />
                <small>Copiar desde LiveKit Cloud o desde tu configuracion del servidor.</small>
              </span>
              <span>
                <input
                  value={provider.settings?.apiSecret || ''}
                  onChange={(event) => updateProviderSetting(provider.id, 'apiSecret', event.target.value)}
                  placeholder="API secret"
                />
                <small>Secreto privado. No debe ir en frontend ni compartirse con pacientes.</small>
              </span>
              <span>
                <input
                  value={provider.settings?.recordingDir || ''}
                  onChange={(event) => updateProviderSetting(provider.id, 'recordingDir', event.target.value)}
                  placeholder="Directorio de grabacion"
                />
              </span>
              <span>
                <input
                  value={provider.settings?.notes || ''}
                  onChange={(event) => updateProviderSetting(provider.id, 'notes', event.target.value)}
                  placeholder="Notas operativas"
                />
              </span>
              <span>
                <label className="platform-provider-toggle">
                  <input
                    type="checkbox"
                    checked={provider.enabled !== false}
                    onChange={(event) => updateProvider(provider.id, 'enabled', event.target.checked)}
                  />
                  <small>Habilitado</small>
                </label>
              </span>
              <button className="btn btn-secondary" type="button" onClick={() => removeProvider(provider.id)}>
                Quitar
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default VideoProviderSettingsPanel;
